/** Compute installment schedule preview from raw inputs. Flat interest. */
function addMonths(base, months) {
  const d = new Date(base.getTime());
  const targetMonth = d.getMonth() + months;
  const targetYear = d.getFullYear() + Math.floor(targetMonth / 12);
  const normalizedMonth = ((targetMonth % 12) + 12) % 12;
  const originalDay = d.getDate();
  const lastDayOfTarget = new Date(targetYear, normalizedMonth + 1, 0).getDate();
  const day = Math.min(originalDay, lastDayOfTarget);
  return new Date(targetYear, normalizedMonth, day);
}

const pad = (n) => String(n).padStart(2, "0");
const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export function computeSchedulePreview({ total_price, down_payment, tenor_months, payment_period_months, annual_rate, sale_date, custom_installment_amount }) {
  const price = Number(total_price || 0);
  const dp = Number(down_payment || 0);
  const tenor = Number(tenor_months || 0);
  const period = Number(payment_period_months || 1);
  const rate = Number(annual_rate || 0);
  const custom = Number(custom_installment_amount || 0);

  const principal = Math.max(price - dp, 0);
  const nPayments = tenor > 0 && period > 0 ? Math.max(1, Math.floor(tenor / period)) : 0;
  const originalTotalInterest = principal * (rate / 100) * (tenor / 12);
  const originalTotalRepay = principal + originalTotalInterest;
  const calculatedPerPayment = nPayments > 0 ? Math.round((originalTotalRepay / nPayments) * 100) / 100 : 0;

  // Effective per-payment (respecting negotiation)
  const isCustom = custom > 0;
  const perPayment = isCustom ? Math.round(custom * 100) / 100 : calculatedPerPayment;
  const totalRepay = isCustom ? Math.round(perPayment * nPayments * 100) / 100 : originalTotalRepay;
  const totalInterest = Math.max(totalRepay - principal, 0);
  const discountPerPayment = isCustom ? Math.round((calculatedPerPayment - perPayment) * 100) / 100 : 0;
  const totalDiscount = isCustom ? Math.round((originalTotalRepay - totalRepay) * 100) / 100 : 0;
  const grandTotal = dp + totalRepay;

  // Build schedule with dates (mirror backend compute_schedule)
  const schedule = [];
  if (nPayments > 0 && sale_date) {
    const start = new Date(sale_date + "T00:00:00");
    for (let i = 0; i < nPayments; i++) {
      const due = addMonths(start, period * (i + 1));
      schedule.push({ sequence: i + 1, due_date: toISO(due), amount: perPayment });
    }
    if (!isCustom && schedule.length > 0) {
      // Absorb rounding on last installment
      const paidSum = schedule.slice(0, -1).reduce((a, x) => a + x.amount, 0);
      schedule[schedule.length - 1].amount = Math.round((totalRepay - paidSum) * 100) / 100;
    }
  }

  return {
    principal, totalInterest, totalRepay, perPayment, nPayments, grandTotal, schedule,
    calculatedPerPayment, originalTotalRepay, discountPerPayment, totalDiscount, isCustom,
  };
}
